import React, { useState, useEffect } from 'react';
import { Modal, Radio, InputNumber, TimePicker, Select, Space } from 'antd';
import dayjs, { Dayjs } from 'dayjs';

const { Option } = Select;

export interface ReminderConfig {
    mode: 'on' | 'before';
    daysBefore?: number;
    time: string; // HH:mm format
    alertType: 'email' | 'popup' | 'both';
    reminderDate?: Date | string;
}

interface ReminderModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (config: ReminderConfig) => void;
    initialValue?: ReminderConfig;
    dueDate?: Date | string | null;
}

const ReminderModal: React.FC<ReminderModalProps> = ({
    open,
    onClose,
    onSave,
    initialValue,
    dueDate
}) => {
    const [mode, setMode] = useState<'on' | 'before'>('before');
    const [daysBefore, setDaysBefore] = useState<number>(0);
    const [time, setTime] = useState<Dayjs>(dayjs('09:00', 'HH:mm'));
    const [alertType, setAlertType] = useState<'email' | 'popup' | 'both'>('both');
    const [reminderDate, setReminderDate] = useState<Dayjs | null>(null);

    useEffect(() => {
        if (initialValue) {
            setMode(initialValue.mode || 'before');
            setDaysBefore(initialValue.daysBefore || 0);
            setTime(initialValue.time ? dayjs(initialValue.time, 'HH:mm') : dayjs('09:00', 'HH:mm'));
            setAlertType(initialValue.alertType || 'both');
            if (initialValue.reminderDate) {
                setReminderDate(dayjs(initialValue.reminderDate));
            }
        } else {
            setMode('before');
            setDaysBefore(0);
            setTime(dayjs('09:00', 'HH:mm'));
            setAlertType('both');
            setReminderDate(null);
        }
    }, [initialValue, open]);

    const handleSave = () => {
        const config: ReminderConfig = {
            mode,
            time: time.format('HH:mm'),
            alertType,
        };

        if (mode === 'before') {
            config.daysBefore = daysBefore;
        } else {
            config.reminderDate = reminderDate?.toDate();
        }

        onSave(config);
        onClose();
    };

    return (
        <Modal
            title="Reminder"
            open={open}
            onCancel={onClose}
            onOk={handleSave}
            okText="Done"
            cancelText="Cancel"
            width={500}
        >
            <div style={{ padding: '16px 0' }}>
                <Radio.Group
                    value={mode}
                    onChange={(e) => setMode(e.target.value)}
                    style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
                >
                    <Radio value="on">
                        <Space>
                            <span>On</span>
                            <InputNumber
                                min={0}
                                value={mode === 'on' ? daysBefore : undefined}
                                onChange={(val) => {
                                    if (mode === 'on') {
                                        setDaysBefore(val || 0);
                                    }
                                }}
                                style={{ width: 60 }}
                                disabled={mode !== 'on'}
                            />
                            <span>Day(s) of due date at</span>
                            <TimePicker
                                value={time}
                                onChange={(val) => setTime(val || dayjs('09:00', 'HH:mm'))}
                                format="HH:mm A"
                                style={{ width: 120 }}
                            />
                        </Space>
                    </Radio>
                    <Radio value="before">
                        <Space>
                            <span>On</span>
                            <InputNumber
                                min={0}
                                value={daysBefore}
                                onChange={(val) => setDaysBefore(val || 0)}
                                style={{ width: 60 }}
                            />
                            <span>Day(s) of due date at</span>
                            <TimePicker
                                value={time}
                                onChange={(val) => setTime(val || dayjs('09:00', 'HH:mm'))}
                                format="HH:mm A"
                                style={{ width: 120 }}
                            />
                        </Space>
                    </Radio>
                </Radio.Group>

                <div style={{ marginTop: 24 }}>
                    <div style={{ marginBottom: 8, fontWeight: 500 }}>Alert</div>
                    <Select
                        value={alertType}
                        onChange={(val) => setAlertType(val)}
                        style={{ width: '100%' }}
                    >
                        <Option value="email">Email</Option>
                        <Option value="popup">Pop-up</Option>
                        <Option value="both">Both</Option>
                    </Select>
                </div>
            </div>
        </Modal>
    );
};

export default ReminderModal;
